import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mockTransactions } from '@/lib/mock-data';
import type { Transaction, TransactionType, TransactionStatus } from '@/types/asset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { dataService, isDemoMode } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Download, Columns, Filter, FileSpreadsheet, FileText, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { exportToCSV, exportToExcel, generateFilename, formatDateForExport, formatCurrencyForExport, formatNumberForExport, type ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { ConfigureColumnsDialog } from '@/components/configure-columns-dialog';
import type { ColumnConfig } from '@/components/configure-columns-dialog';
import { useColumnConfig } from '@/hooks/use-column-config';
import { DEFAULT_ENQUIRY_COLUMNS, ENQUIRY_COLUMNS_STORAGE_KEY } from '@/lib/enquiry-columns-config';

// Extended transaction type with all required fields
interface ExtendedTransaction extends Transaction {
  auditNo: string;
  assetCode: string;
  reference: string;
  year: number;
  period: number;
  startNBV: number;
  movementValue: number;
  depreciationMovement: number;
  endNBV: number;
  detail: string;
  targetYear?: number;
  targetPeriod?: number;
  subLedger?: string;
  category?: string;
  percentage?: number;
  transferredTo?: string;
  transferredFrom?: string;
  project?: string;
  costCentre?: string;
  disposalReason?: string;
  user?: string;
}

// Mock extended transactions
const mockExtendedTransactions: ExtendedTransaction[] = mockTransactions.map((txn, index) => ({
  ...txn,
  auditNo: String(index + 1),
  assetCode: txn.assetId,
  reference: `REF-${String(index + 1).padStart(4, '0')}`,
  year: new Date(txn.transactionDate).getFullYear(),
  period: new Date(txn.transactionDate).getMonth() + 1,
  startNBV: txn.amount * 1.2,
  movementValue: txn.amount,
  depreciationMovement: txn.type === 'depreciation' ? txn.amount : 0,
  endNBV: txn.amount * 0.8,
  detail: txn.description || `${txn.type} transaction`,
  targetYear: txn.type === 'transfer' ? new Date(txn.transactionDate).getFullYear() + 1 : undefined,
  targetPeriod: txn.type === 'transfer' ? new Date(txn.transactionDate).getMonth() + 2 : undefined,
  subLedger: ['FA', 'IT', 'PROP'][index % 3],
  category: txn.assetName.includes('Laptop') ? 'Computer Equipment' : 
            txn.assetName.includes('Desk') ? 'Furniture & Fixtures' : 
            txn.assetName.includes('Vehicle') ? 'Vehicles' : 'Machinery',
  percentage: txn.type === 'depreciation' ? 20 : undefined,
  transferredTo: txn.type === 'transfer' ? 'London HQ' : undefined,
  transferredFrom: txn.type === 'transfer' ? 'Manchester Office' : undefined,
  project: ['Project Alpha', 'Project Beta', undefined][index % 3],
  costCentre: ['CC-001', 'CC-002', 'CC-003'][index % 3],
  disposalReason: txn.type === 'disposal' ? 'End of useful life' : undefined,
  user: txn.preparedBy,
}));

export function Enquiry(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const { mode } = useMode();
  const [transactions, setTransactions] = useState<ExtendedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null);
  const transactionRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  
  // Column configuration with persistence
  const {
    columns: columnConfigs,
    columnVisibility,
    visibleColumns,
    updateColumns,
    resetColumns,
  } = useColumnConfig({
    storageKey: ENQUIRY_COLUMNS_STORAGE_KEY,
    defaultColumns: DEFAULT_ENQUIRY_COLUMNS,
  });
  
  const [configureColumnsOpen, setConfigureColumnsOpen] = useState(false);
  const [filters, setFilters] = useState<{
    type?: TransactionType[];
    status?: TransactionStatus[];
    assetIds?: string[];
  }>({});
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ExtendedTransaction;
    direction: 'asc' | 'desc';
  }>({
    key: 'auditNo',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  useEffect(() => {
    async function loadTransactions() {
      try {
        setLoading(true);
        setError(null);
        
        if (isDemoMode()) {
          setTransactions(mockExtendedTransactions);
        } else {
          const result = await dataService.getTransactions({ pageSize: 1000 });
          const apiTransactions = result.data.map((txn: any) => ({
            ...txn,
            auditNo: txn.auditNumber?.toString() || '',
            assetCode: txn.assetCode || txn.assetId,
            reference: txn.reference || '',
            year: txn.year || new Date(txn.transactionDate).getFullYear(),
            period: txn.period || new Date(txn.transactionDate).getMonth() + 1,
            startNBV: txn.startNBV || 0,
            movementValue: txn.movementValue || txn.amount,
            depreciationMovement: txn.depreciationMovement || 0,
            endNBV: txn.endNBV || 0,
            detail: txn.detail || txn.description || `${txn.type} transaction`,
            targetYear: txn.targetYear,
            targetPeriod: txn.targetPeriod,
            subLedger: txn.subLedger,
            category: txn.category,
            percentage: txn.percentage,
            transferredTo: txn.transferredTo,
            transferredFrom: txn.transferredFrom,
            project: txn.project,
            costCentre: txn.costCentre,
            disposalReason: txn.disposalReason,
            user: txn.preparedBy,
          }));
          setTransactions(apiTransactions);
        }
      } catch (err) {
        console.error('Failed to load transactions:', err);
        setError('Failed to load transactions. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadTransactions();
  }, [mode]);

  // Handle asset IDs from URL params
  useEffect(() => {
    const assetIdsParam = searchParams.get('assetIds');
    if (assetIdsParam) {
      const assetIds = assetIdsParam.split(',');
      setFilters((prev) => ({ ...prev, assetIds }));
    }
  }, [searchParams]);

  // Handle transaction ID from URL params - scroll to and highlight specific transaction
  useEffect(() => {
    const transactionIdParam = searchParams.get('transactionId');
    if (transactionIdParam && transactions.length > 0 && !loading) {
      setHighlightedTransactionId(transactionIdParam);
      
      // Wait for the DOM to update, then scroll to the transaction
      setTimeout(() => {
        const transactionRow = transactionRefs.current[transactionIdParam];
        if (transactionRow) {
          transactionRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedTransactionId(null);
      }, 3000);
    }
  }, [searchParams, transactions, loading]);

  // Filter transactions
  const filteredTransactions = transactions.filter((txn) => {
    // Asset ID filter (from URL params)
    if (filters.assetIds && filters.assetIds.length > 0) {
      const matchesAssetId = filters.assetIds.some(id => 
        txn.assetId.includes(id) || id.includes(txn.assetId)
      );
      if (!matchesAssetId) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        txn.assetId.toLowerCase().includes(query) ||
        txn.assetName.toLowerCase().includes(query) ||
        txn.assetCode.toLowerCase().includes(query) ||
        txn.reference.toLowerCase().includes(query) ||
        txn.auditNo.toLowerCase().includes(query) ||
        txn.detail.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filters.type && filters.type.length > 0) {
      if (!filters.type.includes(txn.type)) return false;
    }

    // Status filter
    if (filters.status && filters.status.length > 0) {
      if (!filters.status.includes(txn.status)) return false;
    }

    return true;
  });

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Sort handler
  const handleSort = (key: keyof ExtendedTransaction) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sort icon component
  const SortIcon = ({ columnKey }: { columnKey: keyof ExtendedTransaction }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  // Apply sorting to filtered transactions
  const sortedTransactions = [...filteredTransactions].sort((a: any, b: any) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Special handling for auditNo - extract numeric part
    if (sortConfig.key === 'auditNo') {
      const aNum = parseInt((aValue as string).replace(/\D/g, ''), 10);
      const bNum = parseInt((bValue as string).replace(/\D/g, ''), 10);
      return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
    }

    // Compare values
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // For dates (convert strings to dates for comparison)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return sortConfig.direction === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
    }

    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedTransactions.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransactions = sortedTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const handleExportCSV = () => {
    try {
      // Build columns array based on visible columns
      const columns: ExportColumn[] = [];
      
      if (columnVisibility.auditNo) columns.push({ key: 'auditNo', label: 'Audit No.' });
      if (columnVisibility.assetCode) columns.push({ key: 'assetCode', label: 'Asset Code' });
      if (columnVisibility.description) columns.push({ key: 'assetName', label: 'Description' });
      if (columnVisibility.reference) columns.push({ key: 'reference', label: 'Reference' });
      if (columnVisibility.date) columns.push({ key: 'transactionDate', label: 'Date', format: formatDateForExport });
      if (columnVisibility.year) columns.push({ key: 'year', label: 'Year' });
      if (columnVisibility.period) columns.push({ key: 'period', label: 'Period' });
      if (columnVisibility.type) columns.push({ key: 'type', label: 'Type' });
      if (columnVisibility.startNBV) columns.push({ key: 'startNBV', label: 'Start Net Book Value', format: formatCurrencyForExport });
      if (columnVisibility.movementValue) columns.push({ key: 'movementValue', label: 'Movement Value', format: formatCurrencyForExport });
      if (columnVisibility.depreciationMovement) columns.push({ key: 'depreciationMovement', label: 'Depreciation Movement', format: formatCurrencyForExport });
      if (columnVisibility.endNBV) columns.push({ key: 'endNBV', label: 'End Net Book Value', format: formatCurrencyForExport });
      if (columnVisibility.detail) columns.push({ key: 'detail', label: 'Detail' });
      if (columnVisibility.targetYear) columns.push({ key: 'targetYear', label: 'Target Year' });
      if (columnVisibility.targetPeriod) columns.push({ key: 'targetPeriod', label: 'Target Period' });
      if (columnVisibility.subLedger) columns.push({ key: 'subLedger', label: 'Sub Ledger' });
      if (columnVisibility.category) columns.push({ key: 'category', label: 'Category' });
      if (columnVisibility.status) columns.push({ key: 'status', label: 'Status' });
      if (columnVisibility.percentage) columns.push({ key: 'percentage', label: 'Percentage', format: (v) => v ? `${v}%` : '' });
      if (columnVisibility.transferredTo) columns.push({ key: 'transferredTo', label: 'Transferred To' });
      if (columnVisibility.transferredFrom) columns.push({ key: 'transferredFrom', label: 'Transferred From' });
      if (columnVisibility.project) columns.push({ key: 'project', label: 'Project' });
      if (columnVisibility.costCentre) columns.push({ key: 'costCentre', label: 'Cost Centre' });
      if (columnVisibility.disposalReason) columns.push({ key: 'disposalReason', label: 'Disposal Reason' });
      if (columnVisibility.user) columns.push({ key: 'user', label: 'User' });

      exportToCSV({
        filename: generateFilename('transactions-export', 'csv'),
        columns,
        data: sortedTransactions,
      });

      toast.success(`Exported ${sortedTransactions.length} transaction(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      // Build columns array based on visible columns
      const columns: ExportColumn[] = [];
      
      if (columnVisibility.auditNo) columns.push({ key: 'auditNo', label: 'Audit No.' });
      if (columnVisibility.assetCode) columns.push({ key: 'assetCode', label: 'Asset Code' });
      if (columnVisibility.description) columns.push({ key: 'assetName', label: 'Description' });
      if (columnVisibility.reference) columns.push({ key: 'reference', label: 'Reference' });
      if (columnVisibility.date) columns.push({ key: 'transactionDate', label: 'Date', format: formatDateForExport });
      if (columnVisibility.year) columns.push({ key: 'year', label: 'Year' });
      if (columnVisibility.period) columns.push({ key: 'period', label: 'Period' });
      if (columnVisibility.type) columns.push({ key: 'type', label: 'Type' });
      if (columnVisibility.startNBV) columns.push({ key: 'startNBV', label: 'Start Net Book Value', format: formatCurrencyForExport });
      if (columnVisibility.movementValue) columns.push({ key: 'movementValue', label: 'Movement Value', format: formatCurrencyForExport });
      if (columnVisibility.depreciationMovement) columns.push({ key: 'depreciationMovement', label: 'Depreciation Movement', format: formatCurrencyForExport });
      if (columnVisibility.endNBV) columns.push({ key: 'endNBV', label: 'End Net Book Value', format: formatCurrencyForExport });
      if (columnVisibility.detail) columns.push({ key: 'detail', label: 'Detail' });
      if (columnVisibility.targetYear) columns.push({ key: 'targetYear', label: 'Target Year' });
      if (columnVisibility.targetPeriod) columns.push({ key: 'targetPeriod', label: 'Target Period' });
      if (columnVisibility.subLedger) columns.push({ key: 'subLedger', label: 'Sub Ledger' });
      if (columnVisibility.category) columns.push({ key: 'category', label: 'Category' });
      if (columnVisibility.status) columns.push({ key: 'status', label: 'Status' });
      if (columnVisibility.percentage) columns.push({ key: 'percentage', label: 'Percentage', format: (v) => v ? `${v}%` : '' });
      if (columnVisibility.transferredTo) columns.push({ key: 'transferredTo', label: 'Transferred To' });
      if (columnVisibility.transferredFrom) columns.push({ key: 'transferredFrom', label: 'Transferred From' });
      if (columnVisibility.project) columns.push({ key: 'project', label: 'Project' });
      if (columnVisibility.costCentre) columns.push({ key: 'costCentre', label: 'Cost Centre' });
      if (columnVisibility.disposalReason) columns.push({ key: 'disposalReason', label: 'Disposal Reason' });
      if (columnVisibility.user) columns.push({ key: 'user', label: 'User' });

      exportToExcel({
        filename: generateFilename('transactions-export', 'xls'),
        sheetName: 'Transactions',
        columns,
        data: sortedTransactions,
      });

      toast.success(`Exported ${sortedTransactions.length} transaction(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const getStatusBadgeVariant = (status: TransactionStatus) => {
    switch (status) {
      case 'posted':
        return 'default';
      case 'approved':
        return 'secondary';
      case 'pending':
        return 'outline';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Helper function to get sort key for a column
  const getSortKeyForColumn = (columnId: string): keyof ExtendedTransaction | null => {
    const sortKeyMap: Record<string, keyof ExtendedTransaction> = {
      auditNo: 'auditNo',
      assetCode: 'assetCode',
      description: 'assetName',
      reference: 'reference',
      date: 'transactionDate',
      year: 'year',
      period: 'period',
      type: 'type',
      startNBV: 'startNBV',
      movementValue: 'movementValue',
      depreciationMovement: 'depreciationMovement',
      endNBV: 'endNBV',
      detail: 'detail',
      targetYear: 'targetYear',
      targetPeriod: 'targetPeriod',
      subLedger: 'subLedger',
      category: 'category',
      status: 'status',
      percentage: 'percentage',
      transferredTo: 'transferredTo',
      transferredFrom: 'transferredFrom',
      project: 'project',
      costCentre: 'costCentre',
      disposalReason: 'disposalReason',
      user: 'user',
    };
    return sortKeyMap[columnId] || null;
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, transaction: ExtendedTransaction) => {
    switch (columnId) {
      case 'auditNo':
        return <TableCell key={columnId} className="font-medium">{transaction.auditNo}</TableCell>;
      case 'assetCode':
        return <TableCell key={columnId}>{transaction.assetCode}</TableCell>;
      case 'description':
        return <TableCell key={columnId}>{transaction.assetName}</TableCell>;
      case 'reference':
        return <TableCell key={columnId}>{transaction.reference}</TableCell>;
      case 'date':
        return <TableCell key={columnId}>{format(new Date(transaction.transactionDate), 'dd/MM/yyyy')}</TableCell>;
      case 'year':
        return <TableCell key={columnId}>{transaction.year}</TableCell>;
      case 'period':
        return <TableCell key={columnId}>{transaction.period}</TableCell>;
      case 'type':
        return (
          <TableCell key={columnId}>
            <Badge variant="outline" className="capitalize">
              {transaction.type}
            </Badge>
          </TableCell>
        );
      case 'startNBV':
        return <TableCell key={columnId} className="text-right">{formatCurrency(transaction.startNBV)}</TableCell>;
      case 'movementValue':
        return <TableCell key={columnId} className="text-right">{formatCurrency(transaction.movementValue)}</TableCell>;
      case 'depreciationMovement':
        return <TableCell key={columnId} className="text-right">{formatCurrency(transaction.depreciationMovement)}</TableCell>;
      case 'endNBV':
        return <TableCell key={columnId} className="text-right">{formatCurrency(transaction.endNBV)}</TableCell>;
      case 'detail':
        return <TableCell key={columnId}>{transaction.detail}</TableCell>;
      case 'targetYear':
        return <TableCell key={columnId}>{transaction.targetYear || '-'}</TableCell>;
      case 'targetPeriod':
        return <TableCell key={columnId}>{transaction.targetPeriod || '-'}</TableCell>;
      case 'subLedger':
        return <TableCell key={columnId}>{transaction.subLedger || '-'}</TableCell>;
      case 'category':
        return <TableCell key={columnId}>{transaction.category || '-'}</TableCell>;
      case 'status':
        return (
          <TableCell key={columnId}>
            <Badge variant={getStatusBadgeVariant(transaction.status)}>
              {transaction.status}
            </Badge>
          </TableCell>
        );
      case 'percentage':
        return <TableCell key={columnId} className="text-right">{transaction.percentage ? `${transaction.percentage}%` : '-'}</TableCell>;
      case 'transferredTo':
        return <TableCell key={columnId}>{transaction.transferredTo || '-'}</TableCell>;
      case 'transferredFrom':
        return <TableCell key={columnId}>{transaction.transferredFrom || '-'}</TableCell>;
      case 'project':
        return <TableCell key={columnId}>{transaction.project || '-'}</TableCell>;
      case 'costCentre':
        return <TableCell key={columnId}>{transaction.costCentre || '-'}</TableCell>;
      case 'disposalReason':
        return <TableCell key={columnId}>{transaction.disposalReason || '-'}</TableCell>;
      case 'user':
        return <TableCell key={columnId}>{transaction.user || '-'}</TableCell>;
      default:
        return <TableCell key={columnId}>-</TableCell>;
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Search className="h-6 w-6 text-primary" />
              Asset History
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Search and view detailed transaction history for any asset
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Type Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Type
                  {filters.type && filters.type.length > 0 && (
                    <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                      {filters.type.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {['addition', 'depreciation', 'disposal', 'revaluation', 'transfer'].map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={filters.type?.includes(type as TransactionType)}
                    onCheckedChange={(checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        type: checked
                          ? [...(prev.type || []), type as TransactionType]
                          : prev.type?.filter((t) => t !== type),
                      }));
                    }}
                  >
                    <span className="capitalize">{type}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Status
                  {filters.status && filters.status.length > 0 && (
                    <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                      {filters.status.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {['pending', 'approved', 'posted', 'rejected'].map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={filters.status?.includes(status as TransactionStatus)}
                    onCheckedChange={(checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        status: checked
                          ? [...(prev.status || []), status as TransactionStatus]
                          : prev.status?.filter((s) => s !== status),
                      }));
                    }}
                  >
                    <span className="capitalize">{status}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setConfigureColumnsOpen(true)}
            >
              <Columns className="mr-2 h-4 w-4" />
              Columns
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export as</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportCSV}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-2">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium">Loading transactions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium text-destructive">Error</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : (
        <div className="rounded-md border bg-white dark:bg-black">
          <Table containerClassName="max-h-[calc(100vh-258px)]">
            <TableHeader>
              <TableRow>
                {visibleColumns.map((column) => {
                  // Map column IDs to their sort keys
                  const sortKeyMap: Record<string, keyof ExtendedTransaction> = {
                    'auditNo': 'auditNo',
                    'assetCode': 'assetCode',
                    'description': 'assetName',
                    'reference': 'reference',
                    'date': 'transactionDate',
                    'year': 'year',
                    'period': 'period',
                    'type': 'type',
                    'startNBV': 'startNBV',
                    'movementValue': 'movementValue',
                    'depreciationMovement': 'depreciationMovement',
                    'endNBV': 'endNBV',
                  };

                  const sortKey = sortKeyMap[column.id];
                  const isRightAligned = ['startNBV', 'movementValue', 'depreciationMovement', 'endNBV', 'percentage'].includes(column.id);

                  return (
                    <TableHead key={column.id} className={isRightAligned ? 'text-right' : ''}>
                      {sortKey ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent"
                          onClick={() => handleSort(sortKey)}
                        >
                          {column.label}
                          <SortIcon columnKey={sortKey} />
                        </Button>
                      ) : (
                        column.label
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={25}
                    className="h-24 text-center"
                  >
                    No transactions found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTransactions.map((transaction) => (
                  <TableRow 
                    key={transaction.id}
                    ref={(el) => {
                      transactionRefs.current[transaction.id] = el;
                    }}
                    className={highlightedTransactionId === transaction.id ? 'bg-primary/10 transition-colors duration-300' : ''}
                  >
                    {visibleColumns.map((column) => {
                      switch (column.id) {
                        case 'auditNo':
                          return <TableCell key={column.id} className="font-medium">{transaction.auditNo}</TableCell>;
                        case 'assetCode':
                          return <TableCell key={column.id}>{transaction.assetCode}</TableCell>;
                        case 'description':
                          return <TableCell key={column.id}>{transaction.assetName}</TableCell>;
                        case 'reference':
                          return <TableCell key={column.id}>{transaction.reference}</TableCell>;
                        case 'date':
                          return <TableCell key={column.id}>{format(new Date(transaction.transactionDate), 'dd/MM/yyyy')}</TableCell>;
                        case 'year':
                          return <TableCell key={column.id}>{transaction.year}</TableCell>;
                        case 'period':
                          return <TableCell key={column.id}>{transaction.period}</TableCell>;
                        case 'type':
                          return (
                            <TableCell key={column.id}>
                              <Badge variant="outline" className="capitalize">
                                {transaction.type}
                              </Badge>
                            </TableCell>
                          );
                        case 'startNBV':
                          return <TableCell key={column.id} className="text-right">{formatCurrency(transaction.startNBV)}</TableCell>;
                        case 'movementValue':
                          return <TableCell key={column.id} className="text-right">{formatCurrency(transaction.movementValue)}</TableCell>;
                        case 'depreciationMovement':
                          return <TableCell key={column.id} className="text-right">{formatCurrency(transaction.depreciationMovement)}</TableCell>;
                        case 'endNBV':
                          return <TableCell key={column.id} className="text-right">{formatCurrency(transaction.endNBV)}</TableCell>;
                        case 'detail':
                          return <TableCell key={column.id}>{transaction.detail}</TableCell>;
                        case 'targetYear':
                          return <TableCell key={column.id}>{transaction.targetYear || '-'}</TableCell>;
                        case 'targetPeriod':
                          return <TableCell key={column.id}>{transaction.targetPeriod || '-'}</TableCell>;
                        case 'subLedger':
                          return <TableCell key={column.id}>{transaction.subLedger || '-'}</TableCell>;
                        case 'category':
                          return <TableCell key={column.id}>{transaction.category || '-'}</TableCell>;
                        case 'status':
                          return (
                            <TableCell key={column.id}>
                              <Badge variant={getStatusBadgeVariant(transaction.status)}>
                                {transaction.status}
                              </Badge>
                            </TableCell>
                          );
                        case 'percentage':
                          return <TableCell key={column.id} className="text-right">{transaction.percentage ? `${transaction.percentage}%` : '-'}</TableCell>;
                        case 'transferredTo':
                          return <TableCell key={column.id}>{transaction.transferredTo || '-'}</TableCell>;
                        case 'transferredFrom':
                          return <TableCell key={column.id}>{transaction.transferredFrom || '-'}</TableCell>;
                        case 'project':
                          return <TableCell key={column.id}>{transaction.project || '-'}</TableCell>;
                        case 'costCentre':
                          return <TableCell key={column.id}>{transaction.costCentre || '-'}</TableCell>;
                        case 'disposalReason':
                          return <TableCell key={column.id}>{transaction.disposalReason || '-'}</TableCell>;
                        case 'user':
                          return <TableCell key={column.id}>{transaction.user || '-'}</TableCell>;
                        default:
                          return <TableCell key={column.id}>-</TableCell>;
                      }
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {/* Pagination and Summary */}
        {!loading && !error && sortedTransactions.length > 0 && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div>
                Showing {startIndex + 1}-{Math.min(endIndex, sortedTransactions.length)} of {sortedTransactions.length} entries
                {sortedTransactions.length !== transactions.length && ` (filtered from ${transactions.length} total)`}
              </div>
              <div>
                Total Movement Value: {formatCurrency(
                  sortedTransactions.reduce((sum, txn) => sum + txn.movementValue, 0)
                )}
              </div>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configure Columns Dialog */}
      <ConfigureColumnsDialog
        open={configureColumnsOpen}
        onOpenChange={setConfigureColumnsOpen}
        columns={columnConfigs}
        onColumnsChange={updateColumns}
        onReset={resetColumns}
      />
    </div>
  );
}

export default Enquiry;
